import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, TestAgentButton } from '@components';
import { CreateAgentData } from '../types';
import { toAgentCalendarRelations } from '../calendarSelection';
import { useAgentOptions, useScenarioDraft } from '../hooks';
import { useToast } from '@contexts/useToast';
import { useOrganizationFeatures } from '@hooks/useOrganizationFeatures';
import { BasicInfoTab } from './tabs/BasicInfoTab';
import { CallSettingsTab } from './tabs/CallSettingsTab';
import { DataAndApiTab, type DataApiSubTab } from './tabs/DataAndApiTab';
import { PostCallTab } from './tabs/PostCallTab';
import { ConfigurationTab } from './tabs/ConfigurationTab';
import { CallerMemoryTab } from './tabs/CallerMemoryTab';
import { scenarioUsesSystemTool } from './scenario/systemTools';
import { ProcessedErrors } from '@utils/errorHandler';

interface AgentFormProps {
  mode: 'create' | 'edit';
  initialData?: CreateAgentData;
  agentId?: string;
  onSave: (
    data: CreateAgentData
  ) => Promise<
    | { success: boolean; agent?: { id: string; name: string }; errors?: ProcessedErrors }
    | { success: boolean; errors: { general: string[] } }
    | undefined
  >;
}

const AgentForm: React.FC<AgentFormProps> = ({ mode, initialData, agentId, onSave }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    voices,
    knowledgeBases,
    callRoutes,
    phoneNumbers,
    loading: optionsLoading,
    refetch: refetchOptions,
  } = useAgentOptions(mode === 'edit' ? initialData?.phone_number : null);
  const [activeTab, setActiveTab] = useState('basic');
  const [dataApiSubTab, setDataApiSubTab] = useState<DataApiSubTab>('data-extraction');
  const { hasFeature } = useOrganizationFeatures();
  const callFlowManagerEnabled = hasFeature('call_flow_manager');
  // Without the Call-Flow-Manager the scenario is neither loaded nor saved.
  const scenarioDraft = useScenarioDraft(
    mode === 'edit' && callFlowManagerEnabled ? agentId : undefined
  );
  const { showToast } = useToast();
  const defaultFormData: CreateAgentData = {
    name: '',
    voice: null,
    knowledge_base: null,
    call_routes: [],
    phone_number: null,
    intro_message: '',
    prompt: '',
    status: 'inactive',
    variable_extraction: '',
    web_search_enabled: false,
    all_internet: false,
    whitelist_websites: '',
    live_api_calls_enabled: false,
    live_api_calls: [],
    recording_notice_enabled: true,
    in_call_sms_enabled: false,
    in_call_sms_freetext_enabled: false,
    in_call_sms_freetext_prompt: '',
    in_call_sms_policy_prompt: '',
    in_call_sms_max_per_call: 3,
    in_call_sms_templates: [],
    ai_rejection_action: 'hangup',
    ai_rejection_transfer_route: null,
    ai_rejection_transfer_message: '',
    address_validation_enabled: false,
    service_area_postal_codes: [],
    intelligent_conversation_summary: 'no',
    summary_email_recipients: [],
    summary_sms_recipients: [],
    summary_routing_prompt: '',
    summary_include_transcript: false,
    follow_up_enabled: false,
    email_template: null,
    email_follow_up_subject: '',
    email_follow_up_content: '',
    email_follow_up_timing: 'immediately',
    email_follow_up_recipients: [],
    sms_template: null,
    sms_follow_up_content: '',
    sms_follow_up_timing: 'immediately',
    sms_follow_up_recipients: [],
    sms_sender_name: null,
    sms_follow_up_mode: 'template',
    sms_follow_up_prompt: '',
    post_call_api_request_enabled: false,
    post_api_calls: [],
    max_call_time_minutes: 15,
    max_waiting_time_seconds: 15,
    silence_check_in_enabled: false,
    silence_check_in_max_attempts: 2,
    silence_wait_extension_seconds: 120,
    caller_memory_enabled: false,
    caller_memory_greeting_mode: 'passive',
    caller_memory_retention_days: 90,
    prefix_padding: 350,
    silence: 400,
    creativity: 30,
    // Gemini-specific configuration
    start_of_speech_sensitivity: 'low',
    end_of_speech_sensitivity: 'low',
    activity_handling: 'interrupts',
    proactive_audio: true,
    affective_dialog: true,
    context_compression_enabled: false,
    compression_trigger_tokens: 30000,
    // Advanced settings
    call_recording: 'disabled',
    working_hours: '24/7',
    priority_level: 'medium',
    tags: '',
    calendar_integration: '',
  };

  const [formData, setFormData] = useState<CreateAgentData>(initialData || defaultFormData);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState(false);
  const stopTestRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData(prevData => {
        // Only update if the data is actually different
        if (JSON.stringify(prevData) !== JSON.stringify(initialData)) {
          return initialData;
        }
        return prevData;
      });
    }
  }, [mode, initialData]);

  const handleInputChange = (
    field: keyof CreateAgentData,
    value: string | number | boolean | string[] | unknown[]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleBatchInputChange = (updates: Partial<CreateAgentData>) => {
    setFormData(prev => ({ ...prev, ...updates }));

    // Clear errors for updated fields
    const updatedFields = Object.keys(updates) as Array<keyof CreateAgentData>;
    setErrors(prev => {
      const newErrors = { ...prev };
      updatedFields.forEach(field => {
        if (newErrors[field]) {
          delete newErrors[field];
        }
      });
      return newErrors;
    });
  };

  // Get phone numbers from API
  const allPhoneNumbers = phoneNumbers;

  // Transform form data before sending to API
  const transformFormData = (
    data: CreateAgentData
  ): CreateAgentData & { calendars?: string[] | null; scheduling_profiles?: string[] } => {
    const sanitizeNumber = (value: unknown, fallback: number) =>
      typeof value === 'number' && !Number.isNaN(value) ? value : fallback;

    const isFollowUpDisabled = !data.follow_up_enabled;

    return {
      ...data,
      max_call_time_minutes: sanitizeNumber(data.max_call_time_minutes, 30),
      max_waiting_time_seconds: sanitizeNumber(data.max_waiting_time_seconds, 30),
      silence_check_in_enabled: data.silence_check_in_enabled || false,
      silence_check_in_max_attempts: sanitizeNumber(data.silence_check_in_max_attempts, 2),
      silence_wait_extension_seconds: sanitizeNumber(data.silence_wait_extension_seconds, 120),
      caller_memory_enabled: data.caller_memory_enabled || false,
      caller_memory_greeting_mode: data.caller_memory_greeting_mode || 'passive',
      caller_memory_retention_days: sanitizeNumber(data.caller_memory_retention_days, 90),
      voice: data.voice || null,
      knowledge_base: data.knowledge_base || null,
      phone_number: data.phone_number || null,
      name: data.name || '',
      intro_message: data.intro_message || '',
      prompt: data.prompt || '',
      variable_extraction: data.variable_extraction || '',
      web_search_enabled: data.web_search_enabled || false,
      all_internet: data.all_internet || false,
      whitelist_websites: data.whitelist_websites || '',
      recording_notice_enabled: data.recording_notice_enabled ?? true,
      ai_rejection_action: data.ai_rejection_action || 'hangup',
      ai_rejection_transfer_route:
        data.ai_rejection_action === 'transfer' ? data.ai_rejection_transfer_route || null : null,
      ai_rejection_transfer_message:
        data.ai_rejection_action === 'transfer' ? data.ai_rejection_transfer_message || '' : '',
      address_validation_enabled: data.address_validation_enabled || false,
      service_area_postal_codes: data.service_area_postal_codes || [],
      service_area_places: undefined,
      summary_email_recipients:
        data.intelligent_conversation_summary === 'no' ? [] : data.summary_email_recipients || [],
      summary_sms_recipients:
        data.intelligent_conversation_summary === 'no' ? [] : data.summary_sms_recipients || [],
      summary_routing_prompt:
        data.intelligent_conversation_summary === 'no' ? '' : data.summary_routing_prompt || '',
      summary_include_transcript:
        data.intelligent_conversation_summary === 'yes_with_email' ||
        data.intelligent_conversation_summary === 'yes_with_email_and_sms'
          ? data.summary_include_transcript || false
          : false,
      email_template: isFollowUpDisabled ? null : data.email_template || null,
      email_follow_up_subject: isFollowUpDisabled ? '' : data.email_follow_up_subject || '',
      email_follow_up_content: isFollowUpDisabled ? '' : data.email_follow_up_content || '',
      email_follow_up_timing: isFollowUpDisabled
        ? 'immediately'
        : data.email_follow_up_timing || 'immediately',
      email_follow_up_recipients: isFollowUpDisabled ? [] : data.email_follow_up_recipients || [],
      sms_template: isFollowUpDisabled ? null : data.sms_template || null,
      sms_follow_up_content: isFollowUpDisabled ? '' : data.sms_follow_up_content || '',
      sms_follow_up_timing: isFollowUpDisabled
        ? 'immediately'
        : data.sms_follow_up_timing || 'immediately',
      sms_follow_up_recipients: isFollowUpDisabled ? [] : data.sms_follow_up_recipients || [],
      sms_sender_name: data.sms_sender_name || null,
      sms_follow_up_mode: isFollowUpDisabled ? 'template' : data.sms_follow_up_mode || 'template',
      sms_follow_up_prompt: isFollowUpDisabled ? '' : data.sms_follow_up_prompt || '',
      call_routes: data.call_routes || [],
      live_api_calls: data.live_api_calls || [],
      post_api_calls: data.post_api_calls || [],
      ...toAgentCalendarRelations(data.calendar_integration),
      working_hours: data.working_hours || '24/7',
      tags: data.tags || '',
    };
  };

  const openScenarioTab = () => {
    setActiveTab('data-api');
    setDataApiSubTab('ai-tools');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrors({});

    // Transform form data before sending
    const transformedData = transformFormData(formData);

    try {
      if (callFlowManagerEnabled && !(await scenarioDraft.prepareSave())) {
        openScenarioTab();
        showToast({
          type: 'error',
          title: t('agents.scenario.tab'),
          message: t('agents.scenario.saveBlocked'),
          duration: 5000,
        });
        return;
      }
      const result = await onSave(transformedData);
      if (result && !result.success) {
        if ('errors' in result && result.errors && 'fieldErrors' in result.errors) {
          setErrors(result.errors.fieldErrors);
        }
      } else if (result && result.success) {
        // Success - form will handle next steps
        setErrors({});
        if (callFlowManagerEnabled && !(await scenarioDraft.commit())) {
          openScenarioTab();
          showToast({
            type: 'error',
            title: t('agents.scenario.tab'),
            message: t('agents.scenario.saveFailed'),
            duration: 5000,
          });
        }
      }
    } catch (error) {
      // No-op; keep field errors cleared
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/agents');
  };

  const tabs = [
    { key: 'basic', label: t('agents.basicInformation'), icon: 'info' },
    { key: 'call-settings', label: t('agents.duringTheCall'), icon: 'summarize' },
    { key: 'post-call', label: t('agents.postCall'), icon: 'settings' },
    { key: 'data-api', label: t('agents.dataAndApiCalls'), icon: 'data_object' },
    { key: 'configuration', label: t('agents.callsAndConfiguration'), icon: 'settings' },
    { key: 'caller-memory', label: t('agents.callerMemory.tabLabel'), icon: 'contacts' },
  ];

  const renderTabContent = () => {
    const commonProps = {
      formData,
      onInputChange: handleInputChange,
      onBatchInputChange: handleBatchInputChange,
      errors,
      voices,
      knowledgeBases,
      callRoutes,
      phoneNumbers: allPhoneNumbers,
      loading: optionsLoading,
      onPhoneNumbersRefresh: refetchOptions,
    };

    switch (activeTab) {
      case 'basic':
        return <BasicInfoTab {...commonProps} />;
      case 'call-settings':
        return (
          <CallSettingsTab
            {...commonProps}
            scenarioUsesAddressValidation={
              callFlowManagerEnabled &&
              scenarioDraft.enabled &&
              scenarioUsesSystemTool(scenarioDraft.currentConfig, 'address_validation')
            }
          />
        );
      case 'data-api':
        return (
          <DataAndApiTab
            {...commonProps}
            agentId={agentId}
            scenario={scenarioDraft}
            callFlowManagerEnabled={callFlowManagerEnabled}
            activeSubTab={dataApiSubTab}
            onSubTabChange={setDataApiSubTab}
          />
        );
      case 'post-call':
        return <PostCallTab {...commonProps} />;
      case 'configuration':
        return <ConfigurationTab {...commonProps} />;
      case 'caller-memory':
        return <CallerMemoryTab formData={formData} agentId={agentId} />;
      default:
        return null;
    }
  };

  const getPageTitle = () => {
    return mode === 'create' ? t('agents.createNewAgent') : t('agents.editAgent');
  };

  const getPageDescription = () => {
    return mode === 'create'
      ? t('agents.configureAgentDescription')
      : t('agents.updateAgentDescription');
  };

  const getButtonText = () => {
    if (loading) {
      return mode === 'create' ? t('agents.creating') : t('agents.updating');
    }
    return mode === 'create' ? t('agents.createAgent') : t('agents.updateAgent');
  };

  return (
    <div className="w-full mx-auto p-6 agent-creation">
      {/* Page Header with Action Buttons */}
      <div className="mb-8 flex items-start justify-between gap-6">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{getPageTitle()}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">{getPageDescription()}</p>
        </div>

        {/* Action Buttons in Header - Only in edit mode */}
        {mode === 'edit' && agentId && (
          <div className="flex gap-3 flex-shrink-0">
            <TestAgentButton
              agentId={agentId}
              agentName={formData.name}
              disabled={loading || optionsLoading}
              testing={testing}
              onTestingChange={setTesting}
              stopTestRef={stopTestRef}
              onSave={handleSubmit}
            />
            <Button variant="gradient-multi" onClick={handleSubmit} disabled={loading}>
              {loading ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          {tabs.map(tab => {
            const isDisabled = tab.key !== 'basic' && mode === 'create';
            const isActive = activeTab === tab.key;

            return (
              <button
                key={tab.key}
                onClick={() => !isDisabled && setActiveTab(tab.key)}
                disabled={isDisabled}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm focus:outline-none ${
                  isActive
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : isDisabled
                      ? 'border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="material-icons text-sm mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="w-full">
        <Card>
          <CardContent className="p-6">
            {mode === 'create' && activeTab !== 'basic' ? (
              <div className="text-center py-12">
                <div className="text-gray-500 dark:text-gray-400 mb-4">
                  <span className="material-icons text-4xl mb-2 block">info</span>
                  <h3 className="text-lg font-semibold mb-2">
                    {t('agents.completeBasicInformationFirst')}
                  </h3>
                  <p className="text-sm">{t('agents.completeBasicInformationDescription')}</p>
                </div>
                <Button variant="gradient-multi" onClick={() => setActiveTab('basic')}>
                  {t('agents.goToBasicInformation')}
                </Button>
              </div>
            ) : (
              renderTabContent()
            )}
          </CardContent>
        </Card>

        {/* Action Buttons at Bottom */}
        {mode === 'create' ? (
          <div className="flex gap-3 mt-6 w-full">
            {activeTab === 'basic' && (
              <>
                <Button
                  variant="danger"
                  onClick={handleCancel}
                  disabled={loading}
                  className="flex-1"
                >
                  {t('agents.cancel')}
                </Button>
                <Button
                  variant="gradient-multi"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1"
                >
                  {getButtonText()}
                </Button>
              </>
            )}
          </div>
        ) : (
          /* Edit mode: Test Agent + Save buttons at bottom (same as header) */
          agentId && (
            <div className="flex gap-3 mt-6 justify-end">
              <TestAgentButton
                agentId={agentId}
                agentName={formData.name}
                disabled={loading || optionsLoading}
                testing={testing}
                onTestingChange={setTesting}
                stopTestRef={stopTestRef}
                onSave={handleSubmit}
              />
              <Button variant="gradient-multi" onClick={handleSubmit} disabled={loading}>
                {loading ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export { AgentForm };
